import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import QueryBuilder from '../components/reporting/QueryBuilder';
import ReportViewer from '../components/reporting/ReportViewer';
import AIInsights from '../components/ai/AIInsights';
import { ReportQuery, ReportResult } from '../services/reportingService';

const ReportingPage: React.FC = () => {
  const [reportResult, setReportResult] = useState<ReportResult | null>(null);
  const [currentQuery, setCurrentQuery] = useState<ReportQuery | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleReportGenerated = useCallback((result: ReportResult) => {
    setReportResult(result);
    setCurrentQuery(result.query);
    setSuccess(`Report generated successfully with ${result.totalCount} records`);
    setError('');
  }, []);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setSuccess('');
  }, []);

  const handleRefresh = useCallback(() => {
    // Re-run the last query
    if (reportResult?.query) {
      // This would be handled by the QueryBuilder component
      setSuccess('Report refreshed');
    }
  }, [reportResult]);

  const handleExport = useCallback((format: string) => {
    setSuccess(`Report exported as ${format.toUpperCase()}`);
  }, []);

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <ReportIcon fontSize="large" color="primary" />
          <Typography variant="h4" component="h1">
            Data Reporting & Analytics
          </Typography>
        </Box>
        
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            href="/"
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <HomeIcon fontSize="inherit" />
            Dashboard
          </Link>
          <Typography color="text.primary" display="flex" alignItems="center" gap={0.5}>
            <ReportIcon fontSize="inherit" />
            Reporting
          </Typography>
        </Breadcrumbs>

        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Build custom reports and analyze your data with AI-powered query assistance and insights.
          Query form submissions, client data, and workflow states regardless of the underlying schema.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Query Builder */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <QueryBuilder
            onReportGenerated={handleReportGenerated}
            onError={handleError}
          />
        </Grid>

        {/* AI Insights */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <AIInsights
            query={currentQuery}
            results={reportResult?.data || null}
            onError={handleError}
          />
        </Grid>

        {/* Report Results */}
        <Grid size={12}>
          <ReportViewer
            reportResult={reportResult}
            onRefresh={handleRefresh}
            onExport={handleExport}
          />
        </Grid>
      </Grid>

      {/* Quick Access Cards */}
      {!reportResult && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Start Guide
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      1. Select Data Source
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose from Form Submissions, Clients, or Loans data
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      2. Add Filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Filter by status, dates, workflow states, or any field
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      3. Run & Export
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generate reports and export in CSV or JSON format
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Sample Queries */}
      {!reportResult && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Sample Queries
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Form Submissions by Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Data Source: Form Submissions<br />
                      • Group By: Status<br />
                      • Aggregation: Count
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Workflow States Over Time
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Data Source: Form Submissions<br />
                      • Date Range: Last 30 days<br />
                      • Group By: Workflow Status
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Client Applications by Industry
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Data Source: Clients<br />
                      • Group By: Industry<br />
                      • Sort By: Count (Descending)
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Failed Submissions Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Data Source: Form Submissions<br />
                      • Filter: Status equals "failed"<br />
                      • Include: Validation Errors
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReportingPage;